import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Stack,
    Checkbox,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import debounce from 'lodash.debounce';
import { MasterConfig, MasterField } from './types';
import { useScope } from '@/hooks/useScope';
import { WRITE_GENERAL_MASTERS } from '@/constants/scopeCodes';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { Surface, Button, Icon, Pill, Note, FilterSelect, confirmColors } from '@/components/ui';
import type { FilterOption } from '@/components/ui';

interface GenericMasterProps<T = any> {
    config: MasterConfig;
}

const GenericMaster = <T extends Record<string, any>>({ config }: GenericMasterProps<T>) => {
    const canWrite = useScope(WRITE_GENERAL_MASTERS);
    const [data, setData] = useState<T[]>([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditItem] = useState<T | null>(null);
    const [formData, setFormData] = useState<Partial<T>>({});

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const [isServerSide, setIsServerSide] = useState(true);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Store dynamically loaded options: { fieldName: optionsArray }
    const [fieldOptions, setFieldOptions] = useState<Record<string, any[]>>({});

    // Debounce search update
    const updateSearchQuery = React.useMemo(
        () => debounce((value: string) => {
            setDebouncedSearchTerm(value);
            setPage(0); // Reset to first page on new search
        }, 500),
        []
    );

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        updateSearchQuery(value);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Pass pagination params
            const response = await config.api.list({
                page: page + 1, // API usually expects 1-based index
                page_size: rowsPerPage,
                search: debouncedSearchTerm
            });

            if (Array.isArray(response)) {
                // Handle non-paginated response (Client-side pagination)
                setData(response);
                setTotalCount(response.length);
                setIsServerSide(false);
            } else if (response && response.results) {
                // Handle paginated response (Server-side pagination)
                setData(response.results);
                setTotalCount(response.count || 0);
                setIsServerSide(true);
            } else {
                setData([]);
                setTotalCount(0);
                setIsServerSide(false);
            }
        } catch (err) {
            console.error(err);
            toast.error(`Failed to fetch ${config.label}`);
        } finally {
            setLoading(false);
        }
    }, [config, page, rowsPerPage, debouncedSearchTerm]);

    // Fetch dynamic options for fields that require them
    useEffect(() => {
        const loadOptions = async () => {
            const newOptions: Record<string, any[]> = {};
            for (const field of config.fields) {
                if (field.optionApi) {
                    try {
                        const results = await field.optionApi({ page_size: 10000, ...field.optionParams });
                        newOptions[field.name] = Array.isArray(results) ? results : (results.results || []);
                    } catch (err) {
                        console.error(`Failed to load options for ${field.name}`, err);
                    }
                }
            }
            setFieldOptions(prev => ({ ...prev, ...newOptions }));
        };
        loadOptions();
    }, [config.fields]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Calculate visible data for rendering
    const visibleData = React.useMemo(() => {
        if (isServerSide) {
            return data;
        }

        let filteredData = data;
        if (debouncedSearchTerm) {
            const lowerSearch = debouncedSearchTerm.toLowerCase();
            filteredData = data.filter(item => {
                return config.fields.some(field => {
                    const val = item[field.name];
                    return val && String(val).toLowerCase().includes(lowerSearch);
                });
            });
        }

        return filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [data, isServerSide, page, rowsPerPage, debouncedSearchTerm, config.fields]);

    const handleOpenDialog = useCallback((item?: T) => {
        const initialData: Partial<T> = {};
        
        // Initialize all fields from config to avoid undefined in payload
        config.fields.forEach(field => {
            if (field.type === 'checkbox') {
                initialData[field.name as keyof T] = (item ? !!item[field.name] : false) as any;
            } else {
                // For edit, only take fields defined in config to strip read-only server fields
                if (item && item[field.name] !== undefined) {
                    initialData[field.name as keyof T] = item[field.name];
                } else {
                    initialData[field.name as keyof T] = (field.multiple ? [] : '') as any;
                }
            }
        });

        if (item) {
            setEditItem(item);
        } else {
            setEditItem(null);
        }
        
        setFormData(initialData);
        setDialogOpen(true);
    }, [config.fields]);

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditItem(null);
        setFormData({});
        setFieldErrors({});
    };

    const handleInputChange = (name: string, value: any) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};
        config.fields.forEach(field => {
            if (field.required) {
                const value = formData[field.name as keyof T];
                // Checkboxes that are required must be true (explicit acknowledgment)
                // Other fields follow standard isEmpty check
                const isEmpty = field.type === 'checkbox' 
                    ? value !== true 
                    : (value === null || value === undefined || value === '');
                
                if (isEmpty) {
                    errors[field.name] = `${field.label} is required`;
                }
            }
        });
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        // Only send fields defined in config
        const payload: Record<string, any> = {};
        config.fields.forEach(field => {
            if (formData[field.name as keyof T] !== undefined) {
                payload[field.name] = formData[field.name as keyof T];
            }
        });

        try {
            if (editingItem) {
                await config.api.update(editingItem.id, payload);
                toast.success(`${config.label} updated successfully`);
            } else {
                await config.api.create(payload);
                toast.success(`${config.label} created successfully`);
            }
            handleCloseDialog();
            fetchData();
        } catch (err) {
            const axiosErr = err as { response?: { data?: unknown }, message?: string };
            const errorData = axiosErr?.response?.data;
            if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
                const backendErrors = errorData as Record<string, unknown>;
                const fieldLevelErrors: Record<string, string> = {};
                const nonFieldMessages: string[] = [];
                for (const [key, value] of Object.entries(backendErrors)) {
                    const message = Array.isArray(value) ? value.join(', ') : String(value);
                    if (key === 'non_field_errors' || key === 'detail') {
                        nonFieldMessages.push(message);
                    } else {
                        fieldLevelErrors[key] = message;
                    }
                }
                if (Object.keys(fieldLevelErrors).length > 0) {
                    setFieldErrors(prev => ({ ...prev, ...fieldLevelErrors }));
                }
                if (nonFieldMessages.length > 0) {
                    toast.error(nonFieldMessages.join('\n'));
                }
            } else {
                const errorMessage = axiosErr.message || `Failed to save ${config.label}`;
                toast.error(errorMessage);
            }
        }
    };

    const handleDelete = useCallback(async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            ...confirmColors('danger'),
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await config.api.delete(id);
                toast.success(`${config.label} deleted successfully`);
                fetchData();
            } catch (err) {
                toast.error(`Failed to delete ${config.label}`);
            }
        }
    }, [config, fetchData]);

    /** The cell value for one config field, after display-key, option-label and
     *  formatter resolution — the same rules the old hand-written table applied. */
    const renderFieldValue = useCallback((field: MasterField, item: T): React.ReactNode => {
        let value = item[field.name];

        // Custom display key (e.g. 'company_name' instead of the 'company' ID)
        if (field.displayKey && item[field.displayKey]) {
            value = item[field.displayKey];
        }

        if (field.type === 'checkbox') {
            return value ? <Pill tone="positive">Yes</Pill> : <Pill tone="neutral">No</Pill>;
        }

        if (field.type === 'select') {
            const options = field.options || fieldOptions[field.name];
            const option = options?.find(o => o.id === value);
            value = option ? option.name : value;
        } else if (field.type === 'autocomplete') {
            // Resolve a bare ID to its label from the fetched options
            if (typeof value !== 'object' && fieldOptions[field.name]) {
                const valKey = field.optionValueKey || 'id';
                const labelKey = field.optionLabelKey || 'name';
                const found = fieldOptions[field.name].find(o => o[valKey] == value);
                if (found) value = found[labelKey];
            }
        }

        if (field.displayFormatter && value != null) {
            value = field.displayFormatter(value);
        }

        return value || '—';
    }, [fieldOptions]);

    const columns = useMemo<ColumnDef<T>[]>(() => {
        const fieldColumns: ColumnDef<T>[] = config.fields.map(field => ({
            key: field.name,
            header: field.label,
            align: field.type === 'number' ? 'right' : undefined,
            render: row => renderFieldValue(field, row),
        }));

        if (!canWrite) return fieldColumns;

        return [
            ...fieldColumns,
            {
                key: '__actions',
                header: 'Actions',
                width: '110px',
                render: row => (
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                        <Button iconOnly onClick={() => handleOpenDialog(row)} title={`Edit ${config.label}`}>
                            <Icon name="edit" size={15} />
                        </Button>
                        <Button iconOnly onClick={() => handleDelete(row.id)} title={`Delete ${config.label}`}>
                            <Icon name="trash" size={15} />
                        </Button>
                    </div>
                ),
            },
        ];
    }, [config.fields, config.label, canWrite, renderFieldValue, handleOpenDialog, handleDelete]);

    const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

    return (
        <>
            <Surface
                title={config.label}
                actions={canWrite ? (
                    <Button
                        variant="primary"
                        size="sm"
                        icon={<Icon name="plus" size={14} />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add New {config.label}
                    </Button>
                ) : undefined}
            >
                <DataTable<T>
                    embedded
                    data={visibleData}
                    columns={columns}
                    rowKey={row => row.id}
                    totalCount={totalCount}
                    loading={loading}
                    currentPage={page + 1}
                    totalPages={totalPages}
                    onPageChange={nextPage => setPage(nextPage - 1)}
                    pageSize={rowsPerPage}
                    pageSizeOptions={[10, 25, 50, 100]}
                    onPageSizeChange={size => { setRowsPerPage(size); setPage(0); }}
                    sortField=""
                    sortDir="asc"
                    onSort={() => undefined}
                    searchQuery={searchTerm}
                    onSearchChange={handleSearchChange}
                    searchPlaceholder={`Search ${config.label}…`}
                    compact
                    loadingMessage={`Loading ${config.label.toLowerCase()}…`}
                    emptyMessage={debouncedSearchTerm ? 'No items match that search.' : 'No items found.'}
                />
            </Surface>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>
                    {editingItem ? `Edit ${config.label}` : `Add New ${config.label}`}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {editingItem && (
                            <Note tone="warning">
                                <strong>Global impact.</strong> Editing this entry updates every{' '}
                                {config.label.toLowerCase()} reference across existing client profiles,
                                reports and transaction records linked to this ID.
                            </Note>
                        )}
                        {config.fields.map(field => {
                            const fieldError = fieldErrors[field.name];

                            if (field.type === 'autocomplete') {
                                const options = field.options || fieldOptions[field.name] || [];
                                const labelKey = field.optionLabelKey || 'name';
                                const valueKey = field.optionValueKey || 'id';
                                const getLabel = (option: any) =>
                                    field.optionLabelKey === 'custom_label_symbol_name'
                                        ? `${option.symbol} - ${option.name}`
                                        : (option[labelKey] || '');
                                const filterOptions: FilterOption[] = options.map((o: any) => ({
                                    value: String(o[valueKey]),
                                    label: getLabel(o),
                                }));
                                const currentValue = formData[field.name as keyof T];
                                const selectedFilterOption = currentValue == null
                                    ? null
                                    : filterOptions.find(o => o.value === String(currentValue)) ?? null;

                                return (
                                    <FormControl key={field.name} fullWidth required={field.required} error={!!fieldError}>
                                        <FilterSelect
                                            label={field.label}
                                            options={filterOptions}
                                            value={selectedFilterOption}
                                            onChange={(opt) => {
                                                if (!opt) {
                                                    handleInputChange(field.name, null);
                                                    return;
                                                }
                                                // Look up the original option so the value keeps its original
                                                // type (often numeric) rather than FilterOption's string value.
                                                const original = options.find((o: any) => String(o[valueKey]) === opt.value);
                                                handleInputChange(field.name, original ? original[valueKey] : opt.value);
                                            }}
                                        />
                                        {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
                                    </FormControl>
                                );
                            }
                            if (field.type === 'select') {
                                const options = field.options || fieldOptions[field.name];
                                return (
                                    <FormControl key={field.name} fullWidth required={field.required} error={!!fieldError}>
                                        <InputLabel>{field.label}</InputLabel>
                                        <Select
                                            value={formData[field.name as keyof T] || (field.multiple ? [] : '')}
                                            label={field.label}
                                            multiple={field.multiple}
                                            onChange={(e) => handleInputChange(field.name, e.target.value)}
                                        >
                                            {options?.map(opt => (
                                                <MenuItem key={opt.id} value={opt.id}>
                                                    {opt.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
                                    </FormControl>
                                );
                            }
                            if (field.type === 'checkbox') {
                                return (
                                    <FormControl key={field.name} error={!!fieldError}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!formData[field.name as keyof T]}
                                                    onChange={(e) => handleInputChange(field.name, e.target.checked)}
                                                />
                                            }
                                            label={field.label}
                                        />
                                        {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
                                    </FormControl>
                                );
                            }
                            return (
                                <TextField
                                    key={field.name}
                                    label={field.label}
                                    type={field.type}
                                    fullWidth
                                    required={field.required}
                                    value={formData[field.name as keyof T] || ''}
                                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                                    error={!!fieldError}
                                    helperText={fieldError}
                                />
                            );
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid var(--border-soft)' }}>
                    <Button variant="ghost" onClick={handleCloseDialog}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave}>
                        {editingItem ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default GenericMaster;
